<template>
  <div class="module-container labs-container">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <div class="d-flex flex-row justify-content-start align-items-center">
        <div
          class="main-image-container bg-purple rounded d-flex justify-content-center align-items-center"
        >
          <img
            height="25"
            width="25"
            :src="require('../../assets/icons/labs.svg')"
          />
        </div>
        <h3 class="mb-0 ml-3">Labs</h3>
      </div>
      <div class="flex">
        <b-button
          class="smaller-button button-custom-blue"
          variant="primary"
          size="sm"
          @click="addLaboratoryOrder"
          ><i class="fa fa-plus mr-2" aria-hidden="true"></i> New Laboratory
          Order</b-button
        >
      </div>
    </div>
    <div class="filters-container" ref="filtersContainer">
      <labs-index-filter
        v-model="filter"
        :hasFilters="hasFilters"
        :loading="loading"
        v-show="showing"
      />
    </div>
    <div class="mt-3 mb-0 w-100 d-flex flex-row justify-content-end">
      <div class="d-flex flex-row justify-content-end align-items-center">
        <top-paginator
          :total="ordersCount"
          :page="filter.page"
          :perPage="filter.pageSize"
          @change-page="changePage"
        />
      </div>
    </div>

    <div
      :style="
        `--labs-filters-container-height:calc(${filtersContainerHeight}px);`
      "
    >
      <grid
        :autoHeight="true"
        :columns="columns"
        :data="ordersItems"
        :store="gridConfig.store"
        :columnLines="gridConfig.columnLines"
        :searchFeature="true"
        @storesort="listeners.storeSort"
        @renderrows="hookDropdownButtons"
        @renderrow="hookDropdownButtons"
      />
    </div>

    <labs-add-order-modal
      v-if="showLabsAddOrderModal"
      v-model="showLabsAddOrderModal"
      @onModify="onModifyAddLaboratoryOrder"
    />
  </div>
</template>
<script>
import { mapState } from "vuex";
import { formatDate } from "../../helpers/date";
import LabsIndexFilter from "./LabsIndexFilter.vue";
import TopPaginator from "../controls/TopPaginator.vue";
import LabsAddOrderModal from "./LabsAddOrderModal.vue";
import Grid from "../../third_party/grid/Grid.vue";
import labsServer from "../../server/labs";

export default {
  name: "LabsIndex",
  components: { LabsIndexFilter, TopPaginator, Grid, LabsAddOrderModal },
  props: ["showing"],
  data() {
    return {
      loading: false,
      showLabsAddOrderModal: false,
      filtersContainerHeight: 0,
      ordersCount: 0,
      orders: [],
      filter: {
        page: 0,
        pageSize: 50,
        searchPhrase: null,
        status: [],
        panel: [],
        collectionDateFrom: null,
        collectionDateTo: null
      },
      listeners: {
        storeSort: sortData => {
          if (sortData.sorters.length == 1) {
            this.changePage(0);
          }
        }
      },
      gridConfig: {
        minHeight: "20em",
        columnLines: false
      }
    };
  },
  methods: {
    async fetchFilteredOrders() {
      this.loading = true;
      const filter = {
        page: this.filter.page,
        pageSize: this.filter.pageSize,
        searchPhrase: this.filter.searchPhrase,
        status: this.filter.status.map(st => {
          return st.value;
        }),
        panelsIds: this.filter.panel.map(panel => {
          return panel.value;
        }),
        collectionDateFrom:
          this.filter.collectionDateFrom == ""
            ? null
            : this.filter.collectionDateFrom,
        collectionDateTo:
          this.filter.collectionDateTo == ""
            ? null
            : this.filter.collectionDateTo
      };
      const result = await labsServer.findAll(filter, this.selectedLocation);
      this.orders = result.records;
      this.ordersCount = result.count;
      this.loading = false;
    },
    changePage(page) {
      this.filter.page = page;
      this.fetchFilteredOrders();
    },
    onModifyAddLaboratoryOrder() {
      this.showLabsAddOrderModal = false;
      this.fetchFilteredOrders();
    },
    addLaboratoryOrder() {
      this.showLabsAddOrderModal = true;
    },
    removeFilter(filter, index) {
      const newValue = [...this.filter[filter]];
      newValue.splice(index, 1);
      this.filter[filter] = newValue;
    },
    computeHeights() {
      setTimeout(() => {
        const filtersContainerHeight = this.$refs.filtersContainer.clientHeight;
        this.filtersContainerHeight = filtersContainerHeight;
      }, 0);
    }
  },
  computed: {
    ...mapState("layout", {
      selectedLocation: state => state.selectedLocation
    }),
    hasFilters() {
      return (
        this.filter.panel.length > 0 ||
        this.filter.status.length > 0 ||
        this.filter.collectionDateFrom ||
        this.filter.collectionDateTo
      );
    },
    ordersItems() {
      return this.orders.map(item => ({
        ...item,
        collectionDateTimeFormat: formatDate(item.collectionDateTime)
      }));
    },
    columns() {
      return [
        {
          text: "Order #",
          field: "refNumber",
          width: 90
        },
        {
          text: "Patient",
          field: "episodeOfCare",
          width: 250,
          type: "template",
          template: data => this.formatEocObj(data.record.data.episodeOfCare)
        },
        {
          text: "Collection Date",
          field: "collectionDateTimeFormat",
          width: 190
        },
        {
          text: "Panel",
          field: "laboratoryPanel",
          width: 180,
          type: "template",
          template: data => {
            return data.record.data.laboratoryPanel
              ? data.record.data.laboratoryPanel.name
              : "Custom";
          }
        },
        {
          text: "Status",
          field: "status",
          width: 140,
          type: "template",
          editor: false,
          template: data => {
            const status = data.record.data.status;
            let className = "warning";
            let statusName = "Sent";
            if (status == "ResultsReceived") {
              className = "success";
              statusName = "Results Received";
            }
            return `<span class="badge-v2 py-2 badge-${className} badge-fixed-size px-1">${statusName}</span>`;
          }
        },
        {
          text: "Tests",
          field: "tests",
          flex: 1,
          type: "template",
          template: data => {
            return data.record.data.tests?.map(x => x.name).join(", ");
          }
        },
        {
          text: "Tools",
          type: "action",
          cellCls: "pointer-cell",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "More",
              renderer: ({ record }) => {
                const actions = [];
                if (record.data.status == "ResultsReceived") {
                  actions.push({
                    content: "View Result",
                    callback: () => {
                      const url = labsServer.viewUrl(record.data.resultFileId);
                      window.open(url, "_blank");
                    }
                  });
                }
                actions.push({
                  content: "Delete",
                  callback: () => {
                    this.$dialog
                      .confirm(
                        {
                          title: "Are you sure you want to delete this record?"
                        },
                        {
                          view: "confirm-delete"
                        }
                      )
                      .then(async () => {
                        let result = await labsServer.delete(record.id);
                        if (result.errorCode) {
                          this.$dialog.alert("Error: " + result.message);
                        } else {
                          this.fetchFilteredOrders();
                          this.showAlertSuccess();
                        }
                      })
                      .catch(function() {});
                  }
                });
                return this.createDropdownButton({
                  items: actions
                });
              }
            }
          ]
        }
      ];
    }
  },
  watch: {
    showing: function(newValue) {
      if (newValue) {
        this.fetchFilteredOrders();
        this.computeHeights();
      }
    },
    filter() {
      if (this.showing) {
        this.filter.page = 0;
        this.fetchFilteredOrders();
        this.computeHeights();
      }
    },
    selectedLocation: function(newValue) {
      if (newValue) {
        this.fetchFilteredOrders();
      }
    }
  }
};
</script>

<style scoped>
.labs-container >>> .b-grid-container .b-container {
  max-height: calc(100vh - var(--labs-filters-container-height) - 240px);
}
.labs-container >>> .b-grid-row {
  background-color: #ffffff;
}
.labs-container >>> .b-grid-row.b-group-row {
  background-color: #f6f6f6;
}
.labs-container >>> .b-action-ct {
  display: flex;
  align-items: center;
}
.labs-container >>> .badge-fixed-size {
  min-width: 120px !important;
}
</style>
